import { useEffect, useMemo, useRef } from "react";

function clamp(v, min, max) {
  return Math.max(min, Math.min(max, v));
}

export default function IdentityFabric({ className }) {
  const canvasRef = useRef(null);
  const rafRef = useRef(null);

  const model = useMemo(() => {
    const nodes = [
      { id: "users", label: "Users", x: 0.1, y: 0.55, r: 9, glow: 14, hue: 210 },
      { id: "devices", label: "Devices", x: 0.23, y: 0.38, r: 8, glow: 12, hue: 195 },
      { id: "apis", label: "APIs", x: 0.36, y: 0.62, r: 8, glow: 12, hue: 190 },
      { id: "workloads", label: "Workloads", x: 0.49, y: 0.40, r: 8, glow: 12, hue: 185 },
      { id: "fabric", label: "Identity Fabric", x: 0.66, y: 0.52, r: 11, glow: 18, hue: 215 },
      { id: "trust", label: "Trust Decisions", x: 0.86, y: 0.48, r: 10, glow: 16, hue: 175 },
    ];

    const edges = [
      { from: "users", to: "devices", strength: 0.55 },
      { from: "devices", to: "apis", strength: 0.55 },
      { from: "apis", to: "workloads", strength: 0.55 },
      { from: "workloads", to: "fabric", strength: 0.85 },
      { from: "fabric", to: "trust", strength: 0.9 },
      { from: "users", to: "fabric", strength: 0.35 },
      { from: "apis", to: "fabric", strength: 0.45 },
    ];

    return { nodes, edges };
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const dpr = Math.max(1, Math.min(2, window.devicePixelRatio || 1));

    const resize = () => {
      const parent = canvas.parentElement;
      if (!parent) return;
      const rect = parent.getBoundingClientRect();
      const w = Math.floor(rect.width);
      const h = Math.floor(rect.height);
      canvas.width = Math.floor(w * dpr);
      canvas.height = Math.floor(h * dpr);
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    resize();

    const onResize = () => resize();
    window.addEventListener("resize", onResize);

    const start = performance.now();

    const draw = (t) => {
      const parent = canvas.parentElement;
      if (!parent) return;

      const rect = parent.getBoundingClientRect();
      const w = rect.width;
      const h = rect.height;

      ctx.clearRect(0, 0, w, h);

      const elapsed = (t - start) / 1000;
      const pulse = 0.5 + 0.5 * Math.sin(elapsed * 1.2);

      const bg = ctx.createLinearGradient(0, 0, w, h);
      bg.addColorStop(0, "rgba(37, 99, 235, 0.12)");
      bg.addColorStop(0.55, "rgba(6, 182, 212, 0.06)");
      bg.addColorStop(1, "rgba(212, 165, 116, 0.05)");
      ctx.fillStyle = bg;
      ctx.fillRect(0, 0, w, h);

      const nodeById = new Map(model.nodes.map((n) => [n.id, n]));

      ctx.lineCap = "round";

      for (const e of model.edges) {
        const a = nodeById.get(e.from);
        const b = nodeById.get(e.to);
        if (!a || !b) continue;

        const ax = a.x * w;
        const ay = a.y * h;
        const bx = b.x * w;
        const by = b.y * h;

        const grad = ctx.createLinearGradient(ax, ay, bx, by);
        grad.addColorStop(0, `rgba(37, 99, 235, ${0.22 * e.strength})`);
        grad.addColorStop(0.5, `rgba(6, 182, 212, ${0.18 * e.strength})`);
        grad.addColorStop(1, `rgba(5, 150, 105, ${0.18 * e.strength})`);

        ctx.strokeStyle = grad;
        ctx.lineWidth = 1 + 1.8 * e.strength;
        ctx.beginPath();
        ctx.moveTo(ax, ay);

        const cx = (ax + bx) / 2;
        const cy = (ay + by) / 2;
        const bend = 0.06 + 0.03 * Math.sin(elapsed * 0.9 + a.x * 6);
        const nx = cx + (by - ay) * bend;
        const ny = cy - (bx - ax) * bend;

        ctx.quadraticCurveTo(nx, ny, bx, by);
        ctx.stroke();

        const travel = (elapsed * (0.18 + 0.08 * e.strength) + a.x * 0.9) % 1;
        const px = ax + (bx - ax) * travel;
        const py = ay + (by - ay) * travel;

        ctx.fillStyle = `rgba(248, 250, 252, ${0.25 + 0.25 * pulse})`;
        ctx.beginPath();
        ctx.arc(px, py, 1.2 + 0.8 * e.strength, 0, Math.PI * 2);
        ctx.fill();
      }

      for (const n of model.nodes) {
        const x = n.x * w;
        const y = n.y * h;
        const wobble = 0.01 * Math.sin(elapsed * 1.1 + n.x * 10);
        const nx = x + wobble * w;
        const ny = y - wobble * h;

        const glow = clamp(n.glow * (0.65 + 0.5 * pulse), 6, 26);
        const outer = ctx.createRadialGradient(nx, ny, 0, nx, ny, glow);
        outer.addColorStop(0, `hsla(${n.hue}, 90%, 65%, ${0.35})`);
        outer.addColorStop(1, "rgba(0,0,0,0)");
        ctx.fillStyle = outer;
        ctx.beginPath();
        ctx.arc(nx, ny, glow, 0, Math.PI * 2);
        ctx.fill();

        ctx.fillStyle = "rgba(248, 250, 252, 0.85)";
        ctx.beginPath();
        ctx.arc(nx, ny, n.r, 0, Math.PI * 2);
        ctx.fill();

        ctx.strokeStyle = "rgba(37, 99, 235, 0.35)";
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.arc(nx, ny, n.r + 2.5, 0, Math.PI * 2);
        ctx.stroke();

        const ring = (elapsed * 0.7 + n.x) % 1;
        const rr = n.r + 4 + ring * 14;
        ctx.strokeStyle = `rgba(6, 182, 212, ${0.18 * (1 - ring)})`;
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.arc(nx, ny, rr, 0, Math.PI * 2);
        ctx.stroke();
      }

      rafRef.current = window.requestAnimationFrame(draw);
    };

    rafRef.current = window.requestAnimationFrame(draw);

    return () => {
      if (rafRef.current) window.cancelAnimationFrame(rafRef.current);
      window.removeEventListener("resize", onResize);
    };
  }, [model.edges, model.nodes]);

  return (
    <canvas
      ref={canvasRef}
      className={className}
      aria-hidden="true"
    />
  );
}
